import React, { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useParams, useNavigate } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
import { Button } from "src/@/components/ui/button";
import { Input } from "src/@/components/ui/input";
import { Card, CardContent } from "src/@/components/ui/card";
import { Switch } from "src/@/components/ui/switch";
import { Label } from "src/@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "src/@/components/ui/select";
import {
    Dialog,
    DialogContent
} from "src/@/components/ui/dialog";
import { Checkbox } from "src/@/components/ui/checkbox";
import { useToast } from "src/@/components/ui/use-toast";
import { Badge } from "src/@/components/ui/badge";
import useAuth from 'src/hooks/useAuth';
import { verificarUrlProvider } from 'src/utils/functions';
import {
    actionBuscarCaixaEntrada,
    actionSalvarCamposCaixa,
    actionTogglePublicarCaixa,
    actionListaStatusChamados,
    actionListaPrioridades,
} from 'src/actions/chamados/actionChamadosConfig';
import StatusTab from 'src/sections/@dashboard/configuracoes/chamados-config/StatusTab';
import PrioridadesTab from 'src/sections/@dashboard/configuracoes/chamados-config/PrioridadesTab';
import { RadioGroup, RadioGroupItem } from "src/@/components/ui/radio-group";
import getActionListaUsuariosByClienteId from 'src/actions/usuarios/getActionListaUsuariosByClienteId';
import getActionListaSetoresByClienteId from 'src/actions/clientes/getActionListaSetoresByClienteId';

const generateId = () => Math.random().toString(36).substr(2, 9);

const FIELD_TYPES = [
    { value: 'short_text', label: 'Resposta Curta', icon: 'mdi:format-text' },
    { value: 'paragraph', label: 'Parágrafo', icon: 'mdi:text-long' },
    { value: 'multiple_choice', label: 'Múltipla Escolha', icon: 'mdi:radiobox-marked' },
    { value: 'checkbox', label: 'Caixas de Seleção', icon: 'mdi:checkbox-marked' },
    { value: 'dropdown', label: 'Lista Suspensa', icon: 'mdi:arrow-down-drop-circle' },
    { value: 'file_upload', label: 'Upload de Arquivo', icon: 'mdi:file-upload-outline' },
];

const TICKET_FIELD_TYPES = [
    { value: 'responsavel', label: 'Responsável', icon: 'mdi:account-outline' },
    { value: 'relator', label: 'Relator', icon: 'mdi:account-outline' },
    { value: 'participantes', label: 'Participantes', icon: 'mdi:account-group-outline' },
    { value: 'organizacao', label: 'Organização', icon: 'mdi:domain' },
    { value: 'prioridade', label: 'Prioridade', icon: 'mdi:sort-variant' },
    { value: 'impacto', label: 'Impacto', icon: 'mdi:arrow-down-drop-circle-outline' },
    { value: 'motivo_pendencia', label: 'Motivo da Pendência', icon: 'mdi:arrow-down-drop-circle-outline' },
    { value: 'motivo_investigacao', label: 'Motivo da Investigação', icon: 'mdi:arrow-down-drop-circle-outline' },
    { value: 'origem', label: 'Origem', icon: 'mdi:arrow-down-drop-circle-outline' },
    { value: 'causa_raiz', label: 'Causa Raiz', icon: 'mdi:format-text' },
    { value: 'solucao_contorno', label: 'Solução de Contorno', icon: 'mdi:format-text' },
    { value: 'categorizacao_produto', label: 'Categorização de Produto', icon: 'mdi:arrow-down-drop-circle-outline' },
    { value: 'categorizacao_operacional', label: 'Categorização Operacional', icon: 'mdi:arrow-down-drop-circle-outline' },
    { value: 'categorias', label: 'Categorias', icon: 'mdi:tag-outline' },
];

const ALL_FIELD_TYPES = [...FIELD_TYPES, ...TICKET_FIELD_TYPES];

const SHORT_TEXT_INPUT_TYPES = [
    { value: 'text', label: 'Texto livre', icon: 'mdi:format-text', placeholder: 'Digite sua resposta...', htmlType: 'text' },
    { value: 'email', label: 'E-mail', icon: 'mdi:email-outline', placeholder: 'seu@email.com', htmlType: 'email' },
    { value: 'cpf', label: 'CPF', icon: 'mdi:card-account-details-outline', placeholder: '000.000.000-00', htmlType: 'text' },
    { value: 'cnpj', label: 'CNPJ', icon: 'mdi:domain', placeholder: '00.000.000/0000-00', htmlType: 'text' },
    { value: 'phone', label: 'Telefone', icon: 'mdi:phone-outline', placeholder: '(00) 00000-0000', htmlType: 'tel' },
    { value: 'number', label: 'Numérico', icon: 'mdi:numeric', placeholder: '0', htmlType: 'number' },
    { value: 'date', label: 'Data', icon: 'mdi:calendar', placeholder: 'dd/mm/aaaa', htmlType: 'date' },
    { value: 'url', label: 'URL / Link', icon: 'mdi:link-variant', placeholder: 'https://...', htmlType: 'url' },
];

export default function ChamadosCaixaBuilder() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { toast } = useToast();
    const { user } = useAuth();

    const [nome, setNome] = useState('Nova Caixa de Entrada');
    const [descricao, setDescricao] = useState('');
    const [campos, setCampos] = useState([]);
    const [activeCampo, setActiveCampo] = useState(null);
    const [isPublished, setIsPublished] = useState(false);
    const [publicId, setPublicId] = useState(null);
    const [caixaStatus, setCaixaStatus] = useState('draft');
    const [settings, setSettings] = useState({
        backgroundColor: '#7C3AED',
        logoUrl: null,
        requireIdentification: true,
        identificationFields: {
            name: { enabled: true, required: true },
            email: { enabled: true, required: true },
            phone: { enabled: false, required: false },
            cpf: { enabled: false, required: false },
        },
        enablePortal: false
    });

    const [direcionamento, setDirecionamento] = useState({
        tipo: 'setor',
        setor_id: null,
        usuario_id: null,
        campo_origem_id: null,
        regras: [],
    });
    const [listaUsuarios, setListaUsuarios] = useState([]);
    const [listaSetores, setListaSetores] = useState([]);

    const [activeTab, setActiveTab] = useState('editor');
    const [previewOpen, setPreviewOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [listaStatus, setListaStatus] = useState([]);
    const [listaPrioridades, setListaPrioridades] = useState([]);

    useEffect(() => {
        if (!id) {
            navigate('/dashboard/configuracoes/chamados-config');
            return;
        }
        loadCaixa();
    }, [id]);

    useEffect(() => {
        if (campos.length > 0 && !activeCampo) {
            setActiveCampo(campos[0].id);
        }
    }, [campos, activeCampo]);

    const loadCaixa = async () => {
        try {
            setLoading(true);
            const response = await actionBuscarCaixaEntrada(id);
            const caixa = response.data.data;
            setNome(caixa.nome || 'Nova Caixa de Entrada');
            setDescricao(caixa.descricao || '');
            setCaixaStatus(caixa.status || 'draft');
            setIsPublished(caixa.isPublished || false);
            setPublicId(caixa.publicId || null);

            const loadedCampos = caixa.campos?.length > 0
                ? caixa.campos
                : [{ id: generateId(), titulo: '', tipo: 'short_text', obrigatorio: false, opcoes: ['Opção 1'] }];
            setCampos(loadedCampos);

            if (caixa.settings) {
                setSettings(prev => ({
                    ...prev,
                    ...caixa.settings,
                    identificationFields: {
                        ...prev.identificationFields,
                        ...(caixa.settings.identificationFields || {})
                    }
                }));
            }

            if (caixa.direcionamento) {
                setDirecionamento({
                    tipo: caixa.direcionamento.tipo || 'setor',
                    setor_id: caixa.direcionamento.setor_id?._id || caixa.direcionamento.setor_id || null,
                    usuario_id: caixa.direcionamento.usuario_id?._id || caixa.direcionamento.usuario_id || null,
                    campo_origem_id: caixa.direcionamento.campo_origem_id || null,
                    regras: (caixa.direcionamento.regras || []).map(r => ({
                        valor: r.valor,
                        tipo: r.tipo || 'setor',
                        setor_id: r.setor_id?._id || r.setor_id || null,
                        usuario_id: r.usuario_id?._id || r.usuario_id || null,
                    })),
                });
            }

            // Carregar listas de setores e usuários
            if (caixa.cliente_id) {
                try {
                    const [setoresRes, usuariosRes] = await Promise.all([
                        getActionListaSetoresByClienteId(caixa.cliente_id),
                        getActionListaUsuariosByClienteId(caixa.cliente_id),
                    ]);
                    setListaSetores(setoresRes.data?.setores || []);
                    setListaUsuarios(usuariosRes.data?.usuarios || []);
                } catch (err) {
                    console.error('Erro ao carregar setores/usuários:', err);
                }
            }
        } catch (error) {
            console.error('Erro ao carregar caixa:', error);
            toast({ title: "Erro", description: "Caixa de entrada não encontrada", variant: "destructive" });
            navigate('/dashboard/configuracoes/chamados-config');
        } finally {
            setLoading(false);
        }
    };

    const buscarStatus = async () => {
        try {
            const response = await actionListaStatusChamados(`?cliente_id=${user.clienteId}&caixa_entrada_id=${id}`);
            setListaStatus(response.data.data);
        } catch (error) {
            console.log(error);
        }
    };

    const buscarPrioridades = async () => {
        try {
            const response = await actionListaPrioridades(`?cliente_id=${user.clienteId}&caixa_entrada_id=${id}`);
            setListaPrioridades(response.data.data);
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        if (id && user.clienteId) {
            buscarStatus();
            buscarPrioridades();
        }
    }, [id, user.clienteId]);

    const handleSave = useCallback(async () => {
        if (!id) return;
        try {
            setSaving(true);
            await actionSalvarCamposCaixa(id, { campos, settings, nome, descricao, direcionamento });
            setHasChanges(false);
            toast({ title: "Salvo!", description: "Caixa de entrada salva com sucesso" });
        } catch (error) {
            console.error('Erro ao salvar:', error);
            toast({ title: "Erro", description: "Não foi possível salvar", variant: "destructive" });
        } finally {
            setSaving(false);
        }
    }, [id, campos, settings, nome, descricao, direcionamento, toast]);

    const handlePublish = async () => {
        if (!id) return;
        try {
            if (hasChanges) await handleSave();
            const newState = !isPublished;
            const response = await actionTogglePublicarCaixa(id, { isPublished: newState });
            const data = response.data.data;
            setIsPublished(newState);
            if (data.publicId) setPublicId(data.publicId);
            if (data.status) setCaixaStatus(data.status);
            toast({
                title: newState ? "Publicado!" : "Despublicado",
                description: newState ? "Sua caixa de entrada está disponível via link público" : "Caixa de entrada despublicada",
            });
        } catch (error) {
            console.error('Erro ao publicar:', error);
            toast({ title: "Erro", description: "Não foi possível alterar publicação", variant: "destructive" });
        }
    };

    const markAsChanged = () => { if (!hasChanges) setHasChanges(true); };

    const handleAddCampo = () => {
        const novo = { id: generateId(), titulo: '', tipo: 'short_text', obrigatorio: false, opcoes: ['Opção 1'] };
        setCampos([...campos, novo]);
        setActiveCampo(novo.id);
        markAsChanged();
    };

    const TICKET_DROPDOWN_TYPES = ['responsavel', 'relator', 'participantes', 'organizacao', 'prioridade', 'impacto', 'motivo_pendencia', 'motivo_investigacao', 'origem', 'categorizacao_produto', 'categorizacao_operacional', 'categorias'];

    const handleUpdateCampo = (campoId, field, value) => {
        setCampos(campos.map(c => {
            if (c.id !== campoId) return c;
            const updated = { ...c, [field]: value };
            if (field === 'tipo' && value === 'file_upload' && !c.fileUploadConfig) {
                updated.fileUploadConfig = { maxFiles: 1, maxFileSize: 10, acceptedTypes: [] };
            }
            if (field === 'tipo' && TICKET_DROPDOWN_TYPES.includes(value) && (!c.opcoes || c.opcoes.length === 0)) {
                updated.opcoes = ['Opção 1'];
            }
            if (field === 'tipo' && TICKET_DROPDOWN_TYPES.includes(value) && !updated.titulo) {
                updated.titulo = ALL_FIELD_TYPES.find(t => t.value === value)?.label || '';
            }
            return updated;
        }));
        markAsChanged();
    };

    const handleAddOption = (campoId) => {
        setCampos(campos.map(c => {
            if (c.id === campoId) {
                return { ...c, opcoes: [...(c.opcoes || []), `Opção ${(c.opcoes?.length || 0) + 1}`] };
            }
            return c;
        }));
        markAsChanged();
    };

    const handleOptionChange = (campoId, index, value) => {
        setCampos(campos.map(c => {
            if (c.id === campoId) {
                const newOpts = [...c.opcoes];
                newOpts[index] = value;
                return { ...c, opcoes: newOpts };
            }
            return c;
        }));
        markAsChanged();
    };

    const handleRemoveOption = (campoId, index) => {
        setCampos(campos.map(c => {
            if (c.id === campoId) {
                return { ...c, opcoes: c.opcoes.filter((_, i) => i !== index) };
            }
            return c;
        }));
        markAsChanged();
    };

    const handleDeleteCampo = (campoId) => {
        const newCampos = campos.filter(c => c.id !== campoId);
        setCampos(newCampos);
        if (activeCampo === campoId && newCampos.length > 0) {
            setActiveCampo(newCampos[newCampos.length - 1].id);
        }
        markAsChanged();
    };

    const handleDuplicateCampo = (campo) => {
        const novo = { ...campo, id: generateId() };
        const idx = campos.findIndex(c => c.id === campo.id);
        const newCampos = [...campos];
        newCampos.splice(idx + 1, 0, novo);
        setCampos(newCampos);
        setActiveCampo(novo.id);
        markAsChanged();
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;
        const items = Array.from(campos);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        setCampos(items);
        markAsChanged();
    };

    const renderCampoInput = (campo) => {
        switch (campo.tipo) {
            case 'short_text': {
                const inputCfg = SHORT_TEXT_INPUT_TYPES.find(t => t.value === campo.inputType) || SHORT_TEXT_INPUT_TYPES[0];
                return (
                    <div className="pt-2 flex items-center gap-2">
                        <Iconify icon={inputCfg.icon} className="text-blue-400/70" width={16} />
                        <input disabled className="w-1/2 bg-transparent text-sm text-blue-400/70 border-b border-blue-400 outline-none py-1" placeholder={inputCfg.placeholder} />
                        {campo.inputType && campo.inputType !== 'text' && (
                            <span className="text-[10px] bg-violet-50 text-violet-600 px-1.5 py-0.5 rounded font-medium">{inputCfg.label}</span>
                        )}
                    </div>
                );
            }
            case 'paragraph':
                return <div className="border-b border-gray-200 py-2"><input disabled className="w-full bg-transparent text-sm text-gray-400" placeholder="Sua resposta..." /></div>;
            case 'multiple_choice':
            case 'checkbox':
            case 'dropdown':
                return (
                    <div className="space-y-2 mt-4">
                        {campo.opcoes?.map((option, index) => (
                            <div key={index} className="flex items-center gap-2 group">
                                <Iconify icon={campo.tipo === 'multiple_choice' ? 'mdi:radiobox-blank' : campo.tipo === 'checkbox' ? 'mdi:checkbox-blank-outline' : 'mdi:numeric'} className="text-slate-400" />
                                <Input value={option} onChange={(e) => handleOptionChange(campo.id, index, e.target.value)} className="h-8 border-transparent hover:border-slate-200 focus:border-violet-500 transition-all" placeholder={`Opção ${index + 1}`} />
                                {campo.opcoes.length > 1 && (
                                    <Button variant="ghost" size="icon" onClick={() => handleRemoveOption(campo.id, index)} className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Iconify icon="mdi:close" className="text-slate-400 hover:text-red-500" />
                                    </Button>
                                )}
                            </div>
                        ))}
                        <div className="flex items-center gap-2 pt-1">
                            <Iconify icon={campo.tipo === 'multiple_choice' ? 'mdi:radiobox-blank' : campo.tipo === 'checkbox' ? 'mdi:checkbox-blank-outline' : 'mdi:numeric'} className="text-slate-400 opacity-50" />
                            <Button variant="ghost" size="sm" onClick={() => handleAddOption(campo.id)} className="text-slate-500 hover:text-violet-600 h-8 font-normal">
                                Adicionar opção
                            </Button>
                        </div>
                    </div>
                );
            case 'file_upload': {
                const cfg = campo.fileUploadConfig || {};
                const acceptLabel = (cfg.acceptedTypes || []).length > 0
                    ? cfg.acceptedTypes.map(t => t.endsWith('/*') ? t.replace('/*', '') : t).join(', ')
                    : 'Todos os tipos';
                return (
                    <div className="mt-3 border-2 border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center gap-2 bg-gray-50/50">
                        <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
                            <Iconify icon="mdi:cloud-upload-outline" width={22} className="text-violet-500" />
                        </div>
                        <p className="text-sm font-medium text-gray-500">Arraste ou clique para enviar</p>
                        <p className="text-[10px] text-gray-400">{acceptLabel} · Máx {cfg.maxFileSize || 10}MB · {cfg.maxFiles || 1} arquivo(s)</p>
                    </div>
                );
            }
            case 'responsavel':
            case 'relator':
            case 'participantes':
            case 'organizacao':
            case 'prioridade':
            case 'impacto':
            case 'motivo_pendencia':
            case 'motivo_investigacao':
            case 'origem':
            case 'categorizacao_produto':
            case 'categorizacao_operacional':
            case 'categorias': {
                const ticketType = ALL_FIELD_TYPES.find(t => t.value === campo.tipo);
                const hasOptions = campo.opcoes?.length > 0 && campo.opcoes.some(o => o.trim());
                return (
                    <div className="mt-3 space-y-2">
                        {hasOptions ? (
                            <div className="space-y-2">
                                {campo.opcoes?.map((option, index) => (
                                    <div key={index} className="flex items-center gap-2 group">
                                        <Iconify icon="mdi:arrow-down-drop-circle-outline" className="text-slate-400" />
                                        <Input value={option} onChange={(e) => handleOptionChange(campo.id, index, e.target.value)} className="h-8 border-transparent hover:border-slate-200 focus:border-violet-500 transition-all" placeholder={`Opção ${index + 1}`} />
                                        {campo.opcoes.length > 1 && (
                                            <Button variant="ghost" size="icon" onClick={() => handleRemoveOption(campo.id, index)} className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Iconify icon="mdi:close" className="text-slate-400 hover:text-red-500" />
                                            </Button>
                                        )}
                                    </div>
                                ))}
                                <div className="flex items-center gap-2 pt-1">
                                    <Iconify icon="mdi:arrow-down-drop-circle-outline" className="text-slate-400 opacity-50" />
                                    <Button variant="ghost" size="sm" onClick={() => handleAddOption(campo.id)} className="text-slate-500 hover:text-violet-600 h-8 font-normal">
                                        Adicionar opção
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-200 rounded-lg bg-gray-50/50">
                                <Iconify icon={ticketType?.icon || 'mdi:form-textbox'} className="text-gray-400" width={16} />
                                <span className="text-xs text-gray-400">Campo do tipo "{ticketType?.label}" — preenchido pelo respondente</span>
                            </div>
                        )}
                    </div>
                );
            }
            case 'causa_raiz':
            case 'solucao_contorno':
                return <div className="border-b border-gray-200 py-2 mt-2"><input disabled className="w-full bg-transparent text-sm text-gray-400" placeholder="Sua resposta..." /></div>;
            default:
                return null;
        }
    };

    // Preview helpers
    const enabledIdentificationFields = settings.requireIdentification
        ? [
            settings.identificationFields.name?.enabled && { key: 'name', label: 'Nome', icon: 'mdi:account-outline', placeholder: 'Seu nome completo', type: 'text', required: settings.identificationFields.name?.required },
            settings.identificationFields.email?.enabled && { key: 'email', label: 'E-mail', icon: 'mdi:email-outline', placeholder: 'seu@email.com', type: 'email', required: settings.identificationFields.email?.required },
            settings.identificationFields.phone?.enabled && { key: 'phone', label: 'Telefone', icon: 'mdi:phone-outline', placeholder: '(00) 00000-0000', type: 'tel', required: settings.identificationFields.phone?.required },
            settings.identificationFields.cpf?.enabled && { key: 'cpf', label: 'CPF', icon: 'mdi:card-account-details-outline', placeholder: '000.000.000-00', type: 'text', required: settings.identificationFields.cpf?.required },
        ].filter(Boolean)
        : [];
    const hasIdentificationStep = enabledIdentificationFields.length > 0;

    const _hexRgb = (hex) => { const h = (hex || '#7C3AED').replace('#', ''); return [parseInt(h.substr(0, 2), 16) || 0, parseInt(h.substr(2, 2), 16) || 0, parseInt(h.substr(4, 2), 16) || 0]; };
    const _mixW = (hex, r) => { const [R, G, B] = _hexRgb(hex); return `rgb(${Math.round(R + (255 - R) * r)}, ${Math.round(G + (255 - G) * r)}, ${Math.round(B + (255 - B) * r)})`; };
    const _darken = (hex, f) => { const [R, G, B] = _hexRgb(hex); return `rgb(${Math.round(R * f)}, ${Math.round(G * f)}, ${Math.round(B * f)})`; };
    const pvAccent = settings.backgroundColor || '#7C3AED';
    const pvIsHex = pvAccent.startsWith('#');
    const pvPageBg = pvIsHex ? _mixW(pvAccent, 0.92) : '#f0ebf8';
    const pvAccentHover = pvIsHex ? _darken(pvAccent, 0.82) : pvAccent;
    const pvAccentSuperLight = pvIsHex ? _mixW(pvAccent, 0.95) : '#f7f3fc';

    const previewCSS = `
        .gfp-input{border:0;border-bottom:1px solid #dadce0;padding:8px 0;font-size:14px;outline:none;background:transparent;width:100%;color:#202124;transition:border-color .2s}
        .gfp-input:focus{border-bottom:2px solid ${pvAccent};margin-bottom:-1px}
        .gfp-input::placeholder{color:#80868b}
        .gfp-textarea{border:0;border-bottom:1px solid #dadce0;padding:8px 0;font-size:14px;outline:none;background:transparent;width:100%;color:#202124;min-height:80px;resize:vertical;transition:border-color .2s}
        .gfp-textarea:focus{border-bottom:2px solid ${pvAccent};margin-bottom:-1px}
        .gfp-textarea::placeholder{color:#80868b}
        .gfp-radio,.gfp-checkbox{accent-color:${pvAccent};cursor:pointer}
        .gfp-opt{display:flex;align-items:center;gap:12px;padding:6px 0;cursor:pointer;border-radius:4px}
        .gfp-opt:hover{background:${pvAccentSuperLight}}
        .gfp-card{background:#fff;border-radius:8px;border:1px solid #dadce0;padding:24px;margin-bottom:12px;transition:box-shadow .2s,border-color .2s}
        .gfp-card:hover{box-shadow:0 1px 3px rgba(0,0,0,.08)}
        .gfp-card:focus-within{border-color:${pvAccent};box-shadow:0 2px 6px rgba(0,0,0,.05)}
        .gfp-header{background:#fff;border-radius:8px;border:1px solid #dadce0;overflow:hidden;margin-bottom:12px}
        .gfp-btn{display:inline-flex;align-items:center;gap:8px;padding:10px 24px;border-radius:4px;font-size:14px;font-weight:500;color:#fff;border:none;cursor:pointer;transition:background .2s,box-shadow .2s;background:${pvAccent}}
        .gfp-btn:hover{background:${pvAccentHover};box-shadow:0 1px 3px rgba(0,0,0,.15)}
        .gfp-upload{border:2px dashed #dadce0;border-radius:8px;padding:24px;text-align:center;cursor:pointer;transition:border-color .2s,background .2s}
        .gfp-upload:hover{border-color:${pvAccent};background:${pvAccentSuperLight}}
    `;

    const renderPreviewField = (campo) => {
        switch (campo.tipo) {
            case 'short_text': {
                const stCfg = SHORT_TEXT_INPUT_TYPES.find(t => t.value === campo.inputType) || SHORT_TEXT_INPUT_TYPES[0];
                return <input type={stCfg.htmlType} placeholder={stCfg.placeholder} className="gfp-input" />;
            }
            case 'paragraph':
                return <textarea placeholder="Sua resposta" className="gfp-textarea" />;
            case 'multiple_choice':
                return (
                    <div className="space-y-1">
                        {campo.opcoes?.map((opt, i) => (
                            <label key={i} className="gfp-opt"><input type="radio" name={`pv-q-${campo.id}`} className="gfp-radio w-[18px] h-[18px]" /><span className="text-sm text-[#202124]">{opt}</span></label>
                        ))}
                    </div>
                );
            case 'checkbox':
                return (
                    <div className="space-y-1">
                        {campo.opcoes?.map((opt, i) => (
                            <label key={i} className="gfp-opt"><input type="checkbox" className="gfp-checkbox w-[18px] h-[18px] rounded" /><span className="text-sm text-[#202124]">{opt}</span></label>
                        ))}
                    </div>
                );
            case 'dropdown':
                return (
                    <Select>
                        <SelectTrigger className="w-full md:w-[300px] border-0 border-b border-[#dadce0] rounded-none px-0 py-2 text-sm text-[#202124] shadow-none focus:ring-0 hover:border-[#80868b] transition-colors h-auto">
                            <SelectValue placeholder="Escolha" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border border-[#dadce0] rounded shadow-lg z-[9999]">
                            {campo.opcoes?.map((opt, i) => (
                                <SelectItem key={i} value={opt} className="py-2 px-3 text-sm text-[#202124] cursor-pointer hover:bg-gray-50">{opt}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                );
            case 'file_upload':
                return (
                    <div className="gfp-upload">
                        <div className="flex flex-col items-center gap-2">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: pvAccentSuperLight }}>
                                <Iconify icon="mdi:cloud-upload-outline" className="w-5 h-5" style={{ color: pvAccent }} />
                            </div>
                            <p className="text-sm font-medium text-[#5f6368]">Clique para enviar arquivo</p>
                            <p className="text-xs text-[#80868b]">Máx. {campo.fileUploadConfig?.maxFileSize || 10}MB — {campo.fileUploadConfig?.maxFiles || 1} arquivo(s)</p>
                        </div>
                    </div>
                );
            case 'responsavel':
            case 'relator':
            case 'participantes':
            case 'organizacao':
            case 'prioridade':
            case 'impacto':
            case 'motivo_pendencia':
            case 'motivo_investigacao':
            case 'origem':
            case 'categorizacao_produto':
            case 'categorizacao_operacional':
            case 'categorias': {
                const hasOpts = campo.opcoes?.length > 0 && campo.opcoes.some(o => o.trim());
                if (hasOpts) {
                    return (
                        <Select>
                            <SelectTrigger className="w-full md:w-[300px] border-0 border-b border-[#dadce0] rounded-none px-0 py-2 text-sm text-[#202124] shadow-none focus:ring-0 hover:border-[#80868b] transition-colors h-auto">
                                <SelectValue placeholder="Escolha" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border border-[#dadce0] rounded shadow-lg z-[9999]">
                                {campo.opcoes?.map((opt, i) => (
                                    <SelectItem key={i} value={opt} className="py-2 px-3 text-sm text-[#202124] cursor-pointer hover:bg-gray-50">{opt}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    );
                }
                return <input placeholder="Sua resposta" className="gfp-input" />;
            }
            case 'causa_raiz':
            case 'solucao_contorno':
                return <textarea placeholder="Sua resposta" className="gfp-textarea" />;
            default: return null;
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col h-full items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <Iconify icon="mdi:loading" className="w-12 h-12 text-[#7C3AED] animate-spin" />
                    <span className="text-gray-500">Carregando caixa de entrada...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden">
            {/* Top Navigation Bar */}
            <header className="bg-white border-b border-gray-200 shrink-0">
                <div className="h-14 px-4 flex items-center gap-3">
                    {/* Back */}
                    <button onClick={() => navigate('/dashboard/configuracoes/chamados-config')} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors shrink-0">
                        <Iconify icon="eva:arrow-ios-back-fill" width={18} className="text-gray-500" />
                    </button>

                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-7 h-7 rounded-lg bg-[#7C3AED]/10 flex items-center justify-center shrink-0">
                            <Iconify icon="eva:inbox-outline" width={15} className="text-[#7C3AED]" />
                        </div>
                        <input
                            value={nome}
                            onChange={(e) => { setNome(e.target.value); markAsChanged(); }}
                            className="text-sm font-semibold text-gray-800 bg-transparent border-0 outline-none focus:ring-0 p-0 min-w-[120px] max-w-[300px] truncate hover:text-[#7C3AED] transition-colors"
                            placeholder="Nome da caixa"
                        />
                    </div>

                    <Badge variant="outline" className={`text-[10px] shrink-0 ${caixaStatus === 'active' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-600 border-amber-200'}`}>
                        {caixaStatus === 'active' ? 'Publicado' : 'Rascunho'}
                    </Badge>

                    {/* Tabs desktop */}
                    <div className="hidden md:flex items-center gap-1 ml-4">
                        {[
                            { key: 'editor', label: 'Editor', icon: 'mdi:pencil-ruler' },
                            { key: 'configurações', label: 'Configurações', icon: 'mdi:cog-outline' },
                            { key: 'estados', label: 'Estados', icon: 'mdi:list-status' },
                            { key: 'prioridades', label: 'Prioridades', icon: 'mdi:sort-variant' },
                        ].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`flex items-center gap-1.5 px-3 h-8 text-xs font-medium rounded-lg transition-colors ${activeTab === tab.key ? 'bg-gray-100 text-gray-800' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'}`}
                            >
                                <Iconify icon={tab.icon} width={14} />
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    <div className="flex-1" />

                    {/* Actions */}
                    {activeTab === 'editor' && (
                        <button
                            className="hidden md:flex items-center gap-1.5 px-3 h-8 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            onClick={() => { setPreviewOpen(true); }}
                        >
                            <Iconify icon="eva:eye-outline" width={15} />
                            Preview
                        </button>
                    )}

                    {activeTab === 'editor' && (
                        <button
                            className="hidden md:flex items-center gap-1.5 px-3 h-8 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors relative"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? <Iconify icon="mdi:loading" width={15} className="animate-spin" /> : <Iconify icon="mdi:content-save-outline" width={15} />}
                            {saving ? 'Salvando...' : 'Salvar'}
                            {hasChanges && !saving && <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-orange-500 rounded-full" />}
                        </button>
                    )}

                    {activeTab === 'editor' && (
                        <>
                            <div className="h-5 w-px bg-gray-200 mx-0.5 shrink-0 hidden md:block" />
                            <button
                                className={`hidden md:flex items-center gap-1.5 px-3.5 h-8 text-xs font-semibold rounded-lg transition-all ${isPublished ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-[#7C3AED] hover:bg-[#6D28D9] text-white shadow-sm shadow-[#7C3AED]/25'}`}
                                onClick={handlePublish}
                                disabled={saving}
                            >
                                <Iconify icon={isPublished ? 'mdi:check-circle-outline' : 'mdi:rocket-launch-outline'} width={14} />
                                {isPublished ? 'Publicado' : 'Publicar'}
                            </button>
                        </>
                    )}
                </div>

                {/* Public link bar */}
                {publicId && activeTab === 'editor' && (
                    <div className="h-9 px-4 flex items-center gap-2 bg-gray-50/80 border-t border-gray-100">
                        <div className="flex items-center gap-1.5 text-gray-400">
                            <Iconify icon="mdi:link-variant" width={14} />
                            <span className="text-[11px] font-medium uppercase tracking-wider">Link público</span>
                        </div>
                        <div className="flex items-center gap-1.5 min-w-0 flex-1">
                            <code className="text-[12px] text-gray-600 font-mono truncate">
                                {`https://link.${verificarUrlProvider()}/chamados/${publicId}`}
                            </code>
                        </div>
                        <button
                            className="flex items-center gap-1.5 px-2.5 h-6 text-[11px] font-medium text-[#7C3AED] hover:bg-[#7C3AED]/10 rounded-md transition-colors shrink-0"
                            onClick={() => { navigator.clipboard.writeText(`https://link.${verificarUrlProvider()}/chamados/${publicId}`); toast({ title: "Link copiado!" }); }}
                        >
                            <Iconify icon="mdi:content-copy" width={12} />
                            Copiar
                        </button>
                        <button
                            className="flex items-center justify-center w-6 h-6 text-gray-400 hover:bg-gray-200/60 rounded-md transition-colors shrink-0"
                            onClick={() => { window.open(`https://link.${verificarUrlProvider()}/chamados/${publicId}`, '_blank'); }}
                            title="Abrir em nova aba"
                        >
                            <Iconify icon="mdi:open-in-new" width={12} />
                        </button>
                    </div>
                )}

                {/* Tabs mobile */}
                <div className="flex md:hidden border-t border-gray-100">
                    {[
                        { key: 'editor', label: 'Editor', icon: 'mdi:pencil-ruler' },
                        { key: 'configurações', label: 'Config.', icon: 'mdi:cog-outline' },
                        { key: 'estados', label: 'Estados', icon: 'mdi:list-status' },
                        { key: 'prioridades', label: 'Prior.', icon: 'mdi:sort-variant' },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${activeTab === tab.key ? 'text-[#7C3AED] border-b-2 border-[#7C3AED]' : 'text-gray-400'}`}
                        >
                            <Iconify icon={tab.icon} width={14} />
                            {tab.label}
                        </button>
                    ))}
                </div>
            </header>

            {/* ==================== TAB EDITOR ==================== */}
            {activeTab === 'editor' && (
                <div className="flex-1 overflow-y-auto">
                    <div className="w-full max-w-3xl mx-auto py-6 px-4 space-y-3">

                        {/* Card título e descrição */}
                        <Card className={`shadow-sm border bg-white overflow-hidden transition-all ${activeCampo === '_header' ? 'border-l-4 border-l-[#7C3AED] border-t-gray-200 border-r-gray-200 border-b-gray-200' : 'border-gray-200'}`}
                            onClick={() => setActiveCampo('_header')}
                        >
                            <div className="h-2.5 bg-[#7C3AED] rounded-t-none" />
                            <CardContent className="p-6">
                                <div className="space-y-3">
                                    <Input
                                        value={nome}
                                        onChange={(e) => { setNome(e.target.value); setHasChanges(true); }}
                                        className="text-2xl font-medium border-transparent hover:border-gray-200 focus:border-[#7C3AED] px-0 h-auto py-2 rounded-none transition-all placeholder:text-gray-300"
                                        placeholder="Nome da caixa de entrada"
                                    />
                                    <Input
                                        value={descricao}
                                        onChange={(e) => { setDescricao(e.target.value); markAsChanged(); }}
                                        className="text-sm text-gray-500 border-transparent hover:border-gray-200 focus:border-[#7C3AED] px-0 h-auto py-1 rounded-none transition-all placeholder:text-gray-400"
                                        placeholder="Descrição da caixa de entrada (opcional)"
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Lista de campos - Drag & Drop */}
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="campos">
                                {(provided) => (
                                    <div className="space-y-3" {...provided.droppableProps} ref={provided.innerRef}>
                                        {campos.map((campo, index) => (
                                            <Draggable key={campo.id} draggableId={campo.id} index={index}>
                                                {(provided, snapshot) => (
                                                    <div ref={provided.innerRef} {...provided.draggableProps} onClick={() => setActiveCampo(campo.id)}>
                                                        <Card className={`shadow-sm bg-white overflow-hidden transition-all ${
                                                            activeCampo === campo.id
                                                                ? 'border-l-4 border-l-[#7C3AED] border-t-gray-200 border-r-gray-200 border-b-gray-200'
                                                                : 'border border-gray-200 hover:border-gray-300'
                                                        } ${snapshot.isDragging ? 'shadow-lg ring-2 ring-[#7C3AED]/20' : ''}`}>
                                                            <CardContent className="p-6">
                                                                {activeCampo === campo.id ? (
                                                                    <div className="space-y-4">
                                                                        <div className="flex items-start gap-4">
                                                                            <div className="flex-1 space-y-1">
                                                                                <Input
                                                                                    value={campo.titulo}
                                                                                    onChange={(e) => handleUpdateCampo(campo.id, 'titulo', e.target.value)}
                                                                                    className="text-base font-medium border-transparent bg-gray-50 hover:border-gray-200 focus:border-[#7C3AED] focus:bg-white px-3 h-12 rounded-lg transition-all placeholder:text-gray-400"
                                                                                    placeholder="Título do campo"
                                                                                />
                                                                                <Input
                                                                                    value={campo.descricao || ''}
                                                                                    onChange={(e) => handleUpdateCampo(campo.id, 'descricao', e.target.value)}
                                                                                    className="text-sm border-transparent hover:border-gray-200 focus:border-[#7C3AED] px-3 h-9 rounded-lg transition-all placeholder:text-gray-400 text-gray-500"
                                                                                    placeholder="Descrição (opcional)"
                                                                                />
                                                                            </div>
                                                                            <div className="w-[200px]">
                                                                                <Select value={campo.tipo} onValueChange={(value) => handleUpdateCampo(campo.id, 'tipo', value)}>
                                                                                    <SelectTrigger className="h-12 bg-gray-50 border-transparent text-gray-600 hover:border-gray-200">
                                                                                        <div className="flex items-center gap-2">
                                                                                            <Iconify icon={ALL_FIELD_TYPES.find(t => t.value === campo.tipo)?.icon || 'mdi:form-textbox'} />
                                                                                            <SelectValue />
                                                                                        </div>
                                                                                    </SelectTrigger>
                                                                                    <SelectContent>
                                                                                        <div className="px-2 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Campos Genéricos</div>
                                                                                        {FIELD_TYPES.map(type => (
                                                                                            <SelectItem key={type.value} value={type.value}>
                                                                                                <div className="flex items-center gap-2">
                                                                                                    <Iconify icon={type.icon} className="text-slate-400" />
                                                                                                    {type.label}
                                                                                                </div>
                                                                                            </SelectItem>
                                                                                        ))}
                                                                                        <div className="px-2 py-1.5 mt-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider border-t border-gray-100">Campos de Ticket</div>
                                                                                        {TICKET_FIELD_TYPES.map(type => (
                                                                                            <SelectItem key={type.value} value={type.value}>
                                                                                                <div className="flex items-center gap-2">
                                                                                                    <Iconify icon={type.icon} className="text-slate-400" />
                                                                                                    {type.label}
                                                                                                </div>
                                                                                            </SelectItem>
                                                                                        ))}
                                                                                    </SelectContent>
                                                                                </Select>
                                                                            </div>
                                                                        </div>

                                                                        {/* Input type selector for short_text */}
                                                                        {campo.tipo === 'short_text' && (
                                                                            <div className="space-y-2">
                                                                                <Label className="text-xs text-gray-500">Tipo de entrada</Label>
                                                                                <div className="flex flex-wrap gap-1.5">
                                                                                    {SHORT_TEXT_INPUT_TYPES.map(t => (
                                                                                        <button
                                                                                            key={t.value}
                                                                                            type="button"
                                                                                            onClick={() => handleUpdateCampo(campo.id, 'inputType', t.value)}
                                                                                            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all border ${
                                                                                                (campo.inputType || 'text') === t.value
                                                                                                    ? 'bg-violet-50 text-violet-700 border-violet-200'
                                                                                                    : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                                                                                            }`}
                                                                                        >
                                                                                            <Iconify icon={t.icon} width={14} />
                                                                                            {t.label}
                                                                                        </button>
                                                                                    ))}
                                                                                </div>
                                                                            </div>
                                                                        )}

                                                                        {/* File upload config */}
                                                                        {campo.tipo === 'file_upload' && (
                                                                            <div className="space-y-3 mt-2 p-3 bg-gray-50 rounded-lg">
                                                                                <Label className="text-xs text-gray-500">Configuração de upload</Label>
                                                                                <div className="grid grid-cols-2 gap-3">
                                                                                    <div>
                                                                                        <Label className="text-[10px] text-gray-400">Máx. arquivos</Label>
                                                                                        <Input
                                                                                            type="number"
                                                                                            min={1}
                                                                                            max={10}
                                                                                            value={campo.fileUploadConfig?.maxFiles || 1}
                                                                                            onChange={(e) => handleUpdateCampo(campo.id, 'fileUploadConfig', { ...campo.fileUploadConfig, maxFiles: parseInt(e.target.value) || 1 })}
                                                                                            className="h-8 text-sm"
                                                                                        />
                                                                                    </div>
                                                                                    <div>
                                                                                        <Label className="text-[10px] text-gray-400">Tamanho máx. (MB)</Label>
                                                                                        <Input
                                                                                            type="number"
                                                                                            min={1}
                                                                                            max={100}
                                                                                            value={campo.fileUploadConfig?.maxFileSize || 10}
                                                                                            onChange={(e) => handleUpdateCampo(campo.id, 'fileUploadConfig', { ...campo.fileUploadConfig, maxFileSize: parseInt(e.target.value) || 10 })}
                                                                                            className="h-8 text-sm"
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}

                                                                        {renderCampoInput(campo)}

                                                                        {/* Actions bar */}
                                                                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                                                                            <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing p-1.5 rounded hover:bg-gray-100 text-gray-400">
                                                                                <Iconify icon="mdi:drag" width={20} />
                                                                            </div>
                                                                            <div className="flex items-center gap-1">
                                                                                <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-violet-600" onClick={(e) => { e.stopPropagation(); handleDuplicateCampo(campo); }} title="Duplicar">
                                                                                    <Iconify icon="mdi:content-copy" width={18} />
                                                                                </Button>
                                                                                <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-red-500" onClick={(e) => { e.stopPropagation(); handleDeleteCampo(campo.id); }} title="Excluir">
                                                                                    <Iconify icon="mdi:trash-can-outline" width={18} />
                                                                                </Button>
                                                                                <div className="w-px h-5 bg-gray-200 mx-1" />
                                                                                <div className="flex items-center gap-2">
                                                                                    <Label htmlFor={`req-${campo.id}`} className="text-xs text-gray-500">Obrigatório</Label>
                                                                                    <Switch id={`req-${campo.id}`} checked={campo.obrigatorio} onCheckedChange={(checked) => handleUpdateCampo(campo.id, 'obrigatorio', checked)} />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="space-y-3">
                                                                        <div>
                                                                            <p className="text-sm text-gray-800">
                                                                                {campo.titulo || 'Campo sem título'}
                                                                                {campo.obrigatorio && <span className="text-red-500 ml-1">*</span>}
                                                                            </p>
                                                                            {campo.descricao && <p className="text-xs text-gray-400 mt-0.5">{campo.descricao}</p>}
                                                                        </div>
                                                                        <div className="opacity-60 pointer-events-none">
                                                                            {renderCampoInput(campo)}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </CardContent>
                                                        </Card>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>

                        {/* Add field button */}
                        <div className="flex justify-center pt-2 pb-6">
                            <Button
                                variant="outline"
                                className="border-dashed border-gray-300 text-gray-500 hover:border-[#7C3AED] hover:text-[#7C3AED] hover:bg-violet-50 px-6"
                                onClick={handleAddCampo}
                            >
                                <Iconify icon="mdi:plus" className="mr-2" />
                                Adicionar campo
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* ==================== TAB CONFIGURAÇÕES ==================== */}
            {activeTab === 'configurações' && (
                <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-slate-50">
                    <div className="max-w-2xl mx-auto space-y-6">

                        {/* Card de Direcionamento do Chamado */}
                        {(() => {
                            const camposOrigem = campos.filter(c => c.tipo === 'origem');
                            const hasOrigem = camposOrigem.length > 0;
                            const campoOrigemAtivo = hasOrigem
                                ? camposOrigem.find(c => c.id === direcionamento.campo_origem_id) || camposOrigem[0]
                                : null;
                            const opcoesOrigem = campoOrigemAtivo?.opcoes?.filter(o => o.trim()) || [];

                            const updateRegra = (valor, field, value) => {
                                setDirecionamento(prev => {
                                    const regras = [...(prev.regras || [])];
                                    const idx = regras.findIndex(r => r.valor === valor);
                                    if (idx >= 0) {
                                        regras[idx] = { ...regras[idx], [field]: value };
                                        if (field === 'tipo') {
                                            regras[idx].setor_id = null;
                                            regras[idx].usuario_id = null;
                                        }
                                    } else {
                                        regras.push({ valor, tipo: 'setor', setor_id: null, usuario_id: null, [field]: value });
                                    }
                                    return { ...prev, regras };
                                });
                                markAsChanged();
                            };

                            return (
                                <Card className="bg-white border border-gray-200">
                                    <CardContent className="p-6 space-y-5">
                                        <div className="flex items-start gap-3">
                                            <div className="p-2.5 bg-violet-100 rounded-lg mt-0.5">
                                                <Iconify icon="eva:navigation-2-outline" className="w-5 h-5 text-violet-600" />
                                            </div>
                                            <div>
                                                <h3 className="text-sm font-semibold text-gray-800">Direcionamento do Chamado</h3>
                                                <p className="text-xs text-gray-500 mt-0.5">
                                                    Configure para onde os chamados desta caixa serão direcionados
                                                </p>
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div className="space-y-2">
                                                <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                    Modo de direcionamento
                                                </Label>
                                                <RadioGroup
                                                    value={direcionamento.tipo}
                                                    onValueChange={(value) => {
                                                        setDirecionamento(prev => ({
                                                            ...prev,
                                                            tipo: value,
                                                            setor_id: null,
                                                            usuario_id: null,
                                                            campo_origem_id: value === 'origem' && campoOrigemAtivo ? campoOrigemAtivo.id : null,
                                                            regras: value === 'origem' ? (prev.regras || []) : [],
                                                        }));
                                                        markAsChanged();
                                                    }}
                                                    className="flex flex-row gap-3"
                                                >
                                                    <label
                                                        htmlFor="dir_setor"
                                                        className={`flex items-center gap-2.5 border rounded-xl p-3 w-full cursor-pointer transition-all ${
                                                            direcionamento.tipo === 'setor'
                                                                ? 'border-[#7C3AED] bg-violet-50/50 ring-1 ring-[#7C3AED]/20'
                                                                : 'border-gray-200 hover:border-gray-300'
                                                        }`}
                                                    >
                                                        <RadioGroupItem value="setor" id="dir_setor" />
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-900">Setor</p>
                                                            <p className="text-[10px] text-gray-500">Fixo</p>
                                                        </div>
                                                    </label>
                                                    <label
                                                        htmlFor="dir_usuario"
                                                        className={`flex items-center gap-2.5 border rounded-xl p-3 w-full cursor-pointer transition-all ${
                                                            direcionamento.tipo === 'usuario'
                                                                ? 'border-[#7C3AED] bg-violet-50/50 ring-1 ring-[#7C3AED]/20'
                                                                : 'border-gray-200 hover:border-gray-300'
                                                        }`}
                                                    >
                                                        <RadioGroupItem value="usuario" id="dir_usuario" />
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-900">Usuário</p>
                                                            <p className="text-[10px] text-gray-500">Específico</p>
                                                        </div>
                                                    </label>
                                                    {hasOrigem && (
                                                        <label
                                                            htmlFor="dir_origem"
                                                            className={`flex items-center gap-2.5 border rounded-xl p-3 w-full cursor-pointer transition-all ${
                                                                direcionamento.tipo === 'origem'
                                                                    ? 'border-[#7C3AED] bg-violet-50/50 ring-1 ring-[#7C3AED]/20'
                                                                    : 'border-gray-200 hover:border-gray-300'
                                                            }`}
                                                        >
                                                            <RadioGroupItem value="origem" id="dir_origem" />
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900">Por Origem</p>
                                                                <p className="text-[10px] text-gray-500">Por opção</p>
                                                            </div>
                                                        </label>
                                                    )}
                                                </RadioGroup>
                                            </div>

                                            {/* Fixo: Setor */}
                                            {direcionamento.tipo === 'setor' && (
                                                <div className="space-y-1.5 animate-in fade-in slide-in-from-top-2 duration-200">
                                                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                        Escolha o Setor
                                                    </Label>
                                                    <Select
                                                        value={direcionamento.setor_id || ''}
                                                        onValueChange={(value) => {
                                                            setDirecionamento(prev => ({ ...prev, setor_id: value }));
                                                            markAsChanged();
                                                        }}
                                                    >
                                                        <SelectTrigger className="rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20">
                                                            <SelectValue placeholder="Selecione o setor..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {listaSetores.map((setor) => (
                                                                <SelectItem key={setor._id} value={setor._id}>
                                                                    {setor.nome_setor}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            )}

                                            {/* Fixo: Usuário */}
                                            {direcionamento.tipo === 'usuario' && (
                                                <div className="space-y-1.5 animate-in fade-in slide-in-from-top-2 duration-200">
                                                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                        Escolha o Usuário
                                                    </Label>
                                                    <Select
                                                        value={direcionamento.usuario_id || ''}
                                                        onValueChange={(value) => {
                                                            setDirecionamento(prev => ({ ...prev, usuario_id: value }));
                                                            markAsChanged();
                                                        }}
                                                    >
                                                        <SelectTrigger className="rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20">
                                                            <SelectValue placeholder="Selecione o usuário..." />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {listaUsuarios.map((usuario) => (
                                                                <SelectItem key={usuario._id} value={usuario._id}>
                                                                    {usuario.nomeFantasia}{usuario.nome_setor ? ` (${usuario.nome_setor})` : ''}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            )}

                                            {/* Por Origem: regras por opção */}
                                            {direcionamento.tipo === 'origem' && (
                                                <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
                                                    {camposOrigem.length > 1 && (
                                                        <div className="space-y-1.5">
                                                            <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                                Campo de Origem
                                                            </Label>
                                                            <Select
                                                                value={direcionamento.campo_origem_id || campoOrigemAtivo?.id || ''}
                                                                onValueChange={(value) => {
                                                                    setDirecionamento(prev => ({ ...prev, campo_origem_id: value, regras: [] }));
                                                                    markAsChanged();
                                                                }}
                                                            >
                                                                <SelectTrigger className="rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20">
                                                                    <SelectValue placeholder="Selecione o campo..." />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {camposOrigem.map(c => (
                                                                        <SelectItem key={c.id} value={c.id}>{c.titulo || 'Origem (sem título)'}</SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                    )}

                                                    {opcoesOrigem.length > 0 ? (
                                                        <div className="space-y-2">
                                                            <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                                Direcionamento por opção
                                                            </Label>
                                                            <div className="space-y-2">
                                                                {opcoesOrigem.map((opcao) => {
                                                                    const regraExistente = direcionamento.regras?.find(r => r.valor === opcao);
                                                                    return (
                                                                        <div key={opcao} className="border border-gray-100 rounded-xl p-3 space-y-2.5 bg-gray-50/50">
                                                                            <div className="flex items-center gap-2">
                                                                                <div className="w-2 h-2 rounded-full bg-violet-400" />
                                                                                <span className="text-sm font-medium text-gray-800">{opcao}</span>
                                                                            </div>
                                                                            <div className="space-y-2">
                                                                                <div>
                                                                                    <Label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1 block">
                                                                                        Direcionar para
                                                                                    </Label>
                                                                                    <Select
                                                                                        value={regraExistente?.tipo || ''}
                                                                                        onValueChange={(value) => updateRegra(opcao, 'tipo', value)}
                                                                                    >
                                                                                        <SelectTrigger className="h-8 text-xs rounded-lg border-gray-200">
                                                                                            <SelectValue placeholder="Selecione setor ou usuário..." />
                                                                                        </SelectTrigger>
                                                                                        <SelectContent>
                                                                                            <SelectItem value="setor">Setor</SelectItem>
                                                                                            <SelectItem value="usuario">Usuário</SelectItem>
                                                                                        </SelectContent>
                                                                                    </Select>
                                                                                </div>
                                                                                {regraExistente?.tipo === 'setor' && (
                                                                                    <div className="animate-in fade-in slide-in-from-top-1 duration-150">
                                                                                        <Label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1 block">
                                                                                            Setor
                                                                                        </Label>
                                                                                        <Select
                                                                                            value={regraExistente.setor_id || ''}
                                                                                            onValueChange={(value) => updateRegra(opcao, 'setor_id', value)}
                                                                                        >
                                                                                            <SelectTrigger className="h-8 text-xs rounded-lg border-gray-200">
                                                                                                <SelectValue placeholder="Selecione o setor..." />
                                                                                            </SelectTrigger>
                                                                                            <SelectContent>
                                                                                                {listaSetores.map((setor) => (
                                                                                                    <SelectItem key={setor._id} value={setor._id}>
                                                                                                        {setor.nome_setor}
                                                                                                    </SelectItem>
                                                                                                ))}
                                                                                            </SelectContent>
                                                                                        </Select>
                                                                                    </div>
                                                                                )}
                                                                                {regraExistente?.tipo === 'usuario' && (
                                                                                    <div className="animate-in fade-in slide-in-from-top-1 duration-150">
                                                                                        <Label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1 block">
                                                                                            Usuário
                                                                                        </Label>
                                                                                        <Select
                                                                                            value={regraExistente.usuario_id || ''}
                                                                                            onValueChange={(value) => updateRegra(opcao, 'usuario_id', value)}
                                                                                        >
                                                                                            <SelectTrigger className="h-8 text-xs rounded-lg border-gray-200">
                                                                                                <SelectValue placeholder="Selecione o usuário..." />
                                                                                            </SelectTrigger>
                                                                                            <SelectContent>
                                                                                                {listaUsuarios.map((usuario) => (
                                                                                                    <SelectItem key={usuario._id} value={usuario._id}>
                                                                                                        {usuario.nomeFantasia}{usuario.nome_setor ? ` (${usuario.nome_setor})` : ''}
                                                                                                    </SelectItem>
                                                                                                ))}
                                                                                            </SelectContent>
                                                                                        </Select>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2 px-3 py-3 border border-dashed border-amber-200 rounded-xl bg-amber-50/50">
                                                            <Iconify icon="mdi:alert-circle-outline" className="w-4 h-4 text-amber-500" />
                                                            <span className="text-xs text-amber-700">
                                                                O campo de origem não possui opções configuradas. Adicione opções no editor do formulário.
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })()}

                        {/* Card de Identificação do Respondente */}
                        <Card className="bg-white border border-gray-200">
                            <CardContent className="p-6 space-y-6">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-start gap-3">
                                        <div className="p-2.5 bg-violet-100 rounded-lg mt-0.5">
                                            <Iconify icon="mdi:account-card-details-outline" className="w-5 h-5 text-violet-600" />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-800">Identificação do Respondente</h3>
                                            <p className="text-xs text-gray-500 mt-0.5">
                                                Exigir que o respondente se identifique antes de preencher o formulário
                                            </p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={settings.requireIdentification}
                                        onCheckedChange={(checked) => { setSettings(prev => ({ ...prev, requireIdentification: checked })); markAsChanged(); }}
                                    />
                                </div>

                                {settings.requireIdentification && (
                                    <div className="space-y-3 pt-2 border-t border-gray-100">
                                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Campos de identificação</p>
                                        {[
                                            { key: 'name', label: 'Nome', icon: 'mdi:account-outline', placeholder: 'Nome completo' },
                                            { key: 'email', label: 'E-mail', icon: 'mdi:email-outline', placeholder: 'email@exemplo.com' },
                                            { key: 'phone', label: 'Telefone', icon: 'mdi:phone-outline', placeholder: '(00) 00000-0000' },
                                            { key: 'cpf', label: 'CPF', icon: 'mdi:card-account-details-outline', placeholder: '000.000.000-00' },
                                        ].map(field => {
                                            const fieldSettings = settings.identificationFields?.[field.key] || { enabled: false, required: false };
                                            return (
                                                <div
                                                    key={field.key}
                                                    className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${fieldSettings.enabled
                                                        ? 'border-violet-200 bg-violet-50/50'
                                                        : 'border-gray-100 bg-gray-50/50'
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className={`p-1.5 rounded-md ${fieldSettings.enabled ? 'bg-violet-100' : 'bg-gray-100'}`}>
                                                            <Iconify icon={field.icon} className={`w-4 h-4 ${fieldSettings.enabled ? 'text-violet-600' : 'text-gray-400'}`} />
                                                        </div>
                                                        <div>
                                                            <p className={`text-sm font-medium ${fieldSettings.enabled ? 'text-gray-800' : 'text-gray-500'}`}>{field.label}</p>
                                                            <p className="text-[10px] text-gray-400">{field.placeholder}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        {fieldSettings.enabled && (
                                                            <label className="flex items-center gap-1.5 cursor-pointer">
                                                                <Checkbox
                                                                    checked={fieldSettings.required}
                                                                    onCheckedChange={(checked) => {
                                                                        setSettings(prev => ({
                                                                            ...prev,
                                                                            identificationFields: { ...prev.identificationFields, [field.key]: { ...prev.identificationFields[field.key], required: !!checked } }
                                                                        }));
                                                                        markAsChanged();
                                                                    }}
                                                                    className="w-3.5 h-3.5 border-violet-300 data-[state=checked]:bg-violet-600 data-[state=checked]:border-violet-600"
                                                                />
                                                                <span className="text-[10px] text-gray-500 font-medium">Obrigatório</span>
                                                            </label>
                                                        )}
                                                        <Switch
                                                            checked={fieldSettings.enabled}
                                                            onCheckedChange={(checked) => {
                                                                setSettings(prev => ({
                                                                    ...prev,
                                                                    identificationFields: { ...prev.identificationFields, [field.key]: { enabled: checked, required: checked ? prev.identificationFields[field.key]?.required : false } }
                                                                }));
                                                                markAsChanged();
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        <p className="text-[10px] text-gray-400 pt-1">
                                            Campos habilitados serão exibidos antes do formulário. Campos obrigatórios impedirão o envio se não preenchidos.
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Card Portal do Respondente */}
                        <Card className="border border-gray-200 shadow-sm">
                            <CardContent className="pt-5 pb-5 space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center">
                                            <Iconify icon="mdi:monitor-dashboard" className="text-violet-600" width={20} />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-800">Portal do Respondente</h3>
                                            <p className="text-xs text-gray-500 mt-0.5">Permite que o respondente acompanhe seus chamados pelo link público</p>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={settings.enablePortal}
                                        onCheckedChange={(checked) => {
                                            setSettings(prev => {
                                                const next = { ...prev, enablePortal: checked };
                                                if (checked) {
                                                    next.requireIdentification = true;
                                                    next.identificationFields = {
                                                        ...prev.identificationFields,
                                                        email: { enabled: true, required: true }
                                                    };
                                                }
                                                return next;
                                            });
                                            markAsChanged();
                                        }}
                                    />
                                </div>
                                {settings.enablePortal && (
                                    <div className="bg-violet-50 border border-violet-100 rounded-lg p-3 flex items-start gap-2">
                                        <Iconify icon="mdi:information-outline" className="text-violet-500 mt-0.5 flex-shrink-0" width={16} />
                                        <p className="text-xs text-violet-700">
                                            O respondente poderá acompanhar seus chamados usando o e-mail informado na identificação. A identificação por e-mail será obrigatória enquanto o portal estiver ativo.
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Botão Salvar */}
                        <div className="flex justify-end">
                            <Button className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white" onClick={handleSave} disabled={saving}>
                                {saving ? (
                                    <><Iconify icon="mdi:loading" className="mr-2 animate-spin" />Salvando...</>
                                ) : (
                                    <><Iconify icon="mdi:content-save" className="mr-2" />Salvar Configurações{hasChanges && <span className="ml-2 w-2 h-2 bg-orange-400 rounded-full"></span>}</>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* ==================== TAB ESTADOS ==================== */}
            {activeTab === 'estados' && (
                <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-slate-50">
                    <div className="max-w-3xl mx-auto">
                        <StatusTab
                            listaStatus={listaStatus}
                            onRefresh={buscarStatus}
                            clienteId={user.clienteId}
                            caixaEntradaId={id}
                        />
                    </div>
                </div>
            )}

            {/* ==================== TAB PRIORIDADES ==================== */}
            {activeTab === 'prioridades' && (
                <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-slate-50">
                    <div className="max-w-3xl mx-auto">
                        <PrioridadesTab
                            listaPrioridades={listaPrioridades}
                            onRefresh={buscarPrioridades}
                            clienteId={user.clienteId}
                            caixaEntradaId={id}
                        />
                    </div>
                </div>
            )}

            {/* ==================== PREVIEW DIALOG ==================== */}
            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="max-w-none w-screen h-screen p-0 border-0 bg-transparent shadow-none shadow-transparent outline-none ring-0">
                    <style>{previewCSS}</style>

                    {/* Google Forms — Normal (List) mode */}
                    <div className="fixed inset-0 z-50 flex flex-col font-sans" style={{ background: pvPageBg }}>
                        {/* Close button */}
                        <button
                            className="absolute top-4 right-4 z-50 w-9 h-9 flex items-center justify-center rounded-full bg-white border border-[#dadce0] hover:bg-gray-50 transition-colors shadow-sm"
                            onClick={() => setPreviewOpen(false)}
                        >
                            <Iconify icon="mdi:close" className="w-4 h-4 text-[#5f6368]" />
                        </button>

                        <div className="h-full overflow-y-auto">
                            <div className="max-w-2xl mx-auto px-4 py-8 md:py-12">

                                {/* Header Card */}
                                <div className="gfp-header">
                                    <div className="h-[10px] rounded-t-lg" style={{ background: pvAccent }} />
                                    <div className="p-6">
                                        {settings.logoUrl && (
                                            <div className="flex justify-center mb-4">
                                                <img src={settings.logoUrl} alt="Logo" className="max-h-20 max-w-[200px] object-contain" />
                                            </div>
                                        )}
                                        <h1 className="text-2xl md:text-[28px] font-normal text-[#202124] leading-snug">
                                            {nome || 'Sem Título'}
                                        </h1>
                                        {descricao && (
                                            <p className="text-sm text-[#202124] mt-3 leading-relaxed whitespace-pre-line">{descricao}</p>
                                        )}
                                        {campos.some(c => c.obrigatorio) && (
                                            <>
                                                <hr className="border-[#dadce0] mt-4 mb-3" />
                                                <p className="text-xs text-[#d93025]">* Indica um campo obrigatório</p>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Identification Card */}
                                {hasIdentificationStep && (
                                    <div className="gfp-card">
                                        <div className="flex items-center gap-2 mb-4">
                                            <Iconify icon="mdi:account-circle-outline" className="w-5 h-5" style={{ color: pvAccent }} />
                                            <h2 className="text-base font-medium text-[#202124]">Identificação</h2>
                                        </div>
                                        <div className="space-y-5">
                                            {enabledIdentificationFields.map(field => (
                                                <div key={field.key}>
                                                    <label className="block text-sm text-[#202124] mb-2">
                                                        {field.label}
                                                        {field.required && <span className="text-[#d93025] ml-1">*</span>}
                                                    </label>
                                                    <input type={field.type} placeholder={field.placeholder} className="gfp-input" />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Campo Cards */}
                                {campos.map((campo) => (
                                    <div key={campo.id} className="gfp-card">
                                        <h2 className="text-base text-[#202124] mb-1">
                                            {campo.titulo || 'Campo sem título'}
                                            {campo.obrigatorio && <span className="text-[#d93025] ml-1">*</span>}
                                        </h2>
                                        {campo.descricao && <p className="text-xs text-[#5f6368] mb-3">{campo.descricao}</p>}
                                        <div className="mt-4">
                                            {renderPreviewField(campo)}
                                        </div>
                                    </div>
                                ))}

                                {/* Submit */}
                                <div className="flex items-center justify-between mt-2 mb-6">
                                    <button className="gfp-btn">Enviar</button>
                                </div>

                                {/* Footer */}
                                <div className="text-center pb-8 space-y-2">
                                    <p className="text-xs text-[#5f6368]">Nunca envie informações sensíveis por formulários.</p>
                                    <p className="text-xs text-[#80868b]">Suas respostas são confidenciais e protegidas.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Floating Color Picker */}
                    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2.5 bg-black/50 backdrop-blur-xl px-4 py-3 rounded-2xl shadow-2xl border border-white/10">
                        <Iconify icon="mdi:palette-outline" className="w-4 h-4 text-white/50 flex-shrink-0" />
                        <div className="flex items-center gap-1.5">
                            {[
                                { color: '#7C3AED', label: 'Violeta' },
                                { color: '#2563EB', label: 'Azul' },
                                { color: '#059669', label: 'Verde' },
                                { color: '#D97706', label: 'Âmbar' },
                                { color: '#DC2626', label: 'Vermelho' },
                                { color: '#DB2777', label: 'Rosa' },
                                { color: '#0F172A', label: 'Escuro' },
                                { color: '#94A3B8', label: 'Cinza' },
                            ].map(preset => (
                                <button
                                    key={preset.color}
                                    type="button"
                                    title={preset.label}
                                    onClick={() => { setSettings(prev => ({ ...prev, backgroundColor: preset.color })); markAsChanged(); }}
                                    className={`w-6 h-6 rounded-full border-2 transition-all hover:scale-125 flex-shrink-0 ${
                                        settings.backgroundColor === preset.color ? 'border-white scale-110 shadow-lg shadow-white/20' : 'border-white/20 hover:border-white/50'
                                    }`}
                                    style={{ backgroundColor: preset.color }}
                                />
                            ))}
                        </div>
                        <div className="w-px h-5 bg-white/20 flex-shrink-0" />
                        <div className="relative flex-shrink-0">
                            <input
                                type="color"
                                value={(() => { const bg = settings.backgroundColor || '#7C3AED'; return bg.startsWith('#') ? bg : '#7C3AED'; })()}
                                onChange={(e) => { setSettings(prev => ({ ...prev, backgroundColor: e.target.value })); markAsChanged(); }}
                                className="w-6 h-6 rounded-full cursor-pointer border-2 border-white/20 hover:border-white/50 transition-all p-0 bg-transparent"
                                title="Cor personalizada"
                            />
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
